/**
 * i18n Provider — Melodimals Web
 *
 * Wraps @lingui/core directly (no community fork dependency) with a
 * SolidJS-native reactive layer. Locale changes update a Signal, which
 * causes all useLingui() consumers to re-derive translations reactively
 * without any component re-render overhead.
 *
 * Lazy-loading: Only the active locale's JS catalog is fetched at startup
 * (and when the user switches language), keeping the initial bundle minimal.
 *
 * Persistence: localStorage only — the app is fully offline and accountless.
 *
 * RTL support: Sets document.documentElement.dir automatically for ar / he.
 */

import { i18n, type MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import { createContext, createSignal, onMount, type ParentComponent, useContext } from "solid-js";
import { RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "@melodimals/config/locales";

// Initialize with a default locale so that early translation calls do not throw errors
i18n.load("en", {});
i18n.activate("en");

// ─── Types ────────────────────────────────────────────────────────────────────

interface I18nContextValue {
  /** Reactive current locale code */
  locale: () => SupportedLocale;
  /** Change locale — reloads catalog and persists to localStorage */
  setLocale: (locale: SupportedLocale) => Promise<void>;
  /** True while the catalog is loading */
  isLoading: () => boolean;
  /**
   * Reactive translation function.
   *
   * TYPE BARRIER (intentionally narrower than the implementation): accepts only
   * `MessageDescriptor`, meaning the result of a macro (`` msg`…` ``). A plain string
   * —`t("Play")`— **does not compile**, which is the important part: Lingui's extractor sees only
   * macros, so a plain string never enters the catalog and remains in English for
   * non-English locales WITHOUT any gate reporting it. The implementation (`translate`)
   * still accepts a string and resolves it by hash, so nothing breaks at runtime; the
   * type makes it impossible to write.
   */
  t: (message: MessageDescriptor, values?: Record<string, unknown>) => string;
  /**
   * Translates a DYNAMIC key: a string that is unknown at the call site because
   * it comes from a data table.
   *
   * It is correct only if that exact text is declared with `` msg`…` `` in a MANIFEST
   * the extractor can see, because then the id hash matches and the catalog resolves it.
   * Without a manifest, the string is NOT extracted and users see it in English for
   * non-English locales.
   *
   * It exists so the escape from `t`'s strict type is EXPLICIT and greppable, instead
   * of a silent `as`. Every use must point to its manifest.
   */
  tDynamic: (message: string, values?: Record<string, unknown>) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue>();

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

// ─── Catalog Loader ───────────────────────────────────────────────────────────

interface CatalogModule {
  messages: Record<string, string>;
}

function assertCatalogModule(mod: unknown): asserts mod is CatalogModule {
  if (typeof mod !== "object" || mod === null || !("messages" in mod)) {
    throw new Error("Invalid catalog module");
  }
}

/** An already imported catalog, ready to be handed to i18n. */
interface FetchedCatalog {
  /** The locale that ACTUALLY loaded (== the request, unless fallback to "en"). */
  locale: SupportedLocale;
  messages: Record<string, string>;
}

/**
 * Dynamically imports the compiled .js catalog for the requested locale.
 * Vite will code-split each locale into a separate chunk automatically.
 *
 * ⚠️ This function is PURE with respect to i18n: it imports and returns, but does
 * NOT call `i18n.load`/`i18n.activate`. It used to, and that was the bug: there were
 * TWO concurrent callers (startup in `onMount` and the user's change in
 * `setLocale`), and neither could discard a stale result because by the time the
 * caller regained control, the side effect on global i18n had ALREADY happened.
 * Now `applyLocale` decides whether the result is still current, and the effect
 * happens AFTER that check.
 */
async function fetchCatalog(locale: SupportedLocale): Promise<FetchedCatalog> {
  try {
    const rawModule: unknown = await import(`../../locales/${locale}/messages.mjs`);
    assertCatalogModule(rawModule);
    return { locale, messages: rawModule.messages };
  } catch (err) {
    console.warn(`[i18n] Failed to load catalog for "${locale}", falling back to "en"`, err);
    // Fall back to English if catalog missing
    const rawEnModule: unknown = await import("../../locales/en/messages.mjs");
    assertCatalogModule(rawEnModule);
    return { locale: "en", messages: rawEnModule.messages };
  }
}

/** Applies correct RTL/LTR direction on <html> element */
function applyDirection(locale: SupportedLocale) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  document.documentElement.lang = locale;
}

/** Clean type-guard to verify if an unknown string represents a SupportedLocale */
function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return typeof locale === "string" && SUPPORTED_LOCALES.some((code) => code === locale);
}

/** Detect browser locale and match against supported locales */
function detectBrowserLocale(): SupportedLocale {
  const browser = navigator.language.split("-")[0]?.toLowerCase() ?? "en";
  return isSupportedLocale(browser) ? browser : "en";
}

const LOCALE_STORAGE_KEY = "@app_locale";

// ─── Provider ─────────────────────────────────────────────────────────────────

export const I18nProvider: ParentComponent = (props) => {
  const [locale, setLocaleSignal] = createSignal<SupportedLocale>("en");
  const [isLoading, setIsLoading] = createSignal(true);
  // Bumped after every REAL catalog load. Consumers (`translate`) depend on it so
  // they re-derive when a catalog finishes loading even if the `locale` signal
  // value didn't change. The module init seeds an EMPTY "en" catalog
  // (`i18n.load("en", {})`) so early calls don't throw; the first real load on
  // mount for a user whose locale IS the default "en" would set the signal to its
  // current value ("en") — a NO-OP that skips reactivity, so consumers keep the
  // empty catalog. In dev the lingui macro inlines each message's default text so
  // strings still render, but in a PRODUCTION build the macro strips those
  // defaults, so those strings render as their raw hash id. Bumping this signal
  // after each load forces the re-derive regardless of whether `locale` changed.
  const [catalogVersion, setCatalogVersion] = createSignal(0);

  // There are TWO catalog-load origins that can overlap: startup
  // (`onMount`) and the user's language change (`setLocale`). Startup's import
  // may still be IN FLIGHT after the user changes language; without a guard,
  // that import lands later and reactivates its language (English),
  // returning the entire UI to English even though the user chose another.
  // It only manifests when the import takes long enough—that is, under load:
  // why it goes unnoticed locally and appears on busy machines.
  // `applyToken` ensures only the newest request can mutate anything
  // ("last write wins").
  let applyToken = 0;

  /**
   * Applies a locale:
   * 1. Fetches the catalog chunk (without touching i18n)
   * 2. Discards the result if a newer request has overtaken it
   * 3. Hands the catalog to i18n (load + activate)
   * 4. Updates the reactive signals + HTML dir/lang attributes
   */
  const applyLocale = async (next: SupportedLocale): Promise<void> => {
    const token = ++applyToken;
    setIsLoading(true);
    const fetched = await fetchCatalog(next); // only the import: does not touch i18n
    if (token !== applyToken) {
      // Overtaken by a newer request (which owns isLoading).
      // The catalog is discarded WITHOUT handing it to i18n: activating it here
      // reverted the language already set by the current request.
      return;
    }
    // Activate the locale that ACTUALLY loaded: if there was a fallback to "en",
    // i18n must reflect "en", not the nonexistent request.
    i18n.load(fetched.locale, fetched.messages);
    i18n.activate(fetched.locale);
    setCatalogVersion((v) => v + 1);
    setLocaleSignal(next);
    applyDirection(next);
    setIsLoading(false);
  };

  /**
   * Changes the active locale:
   * 1. Loads the catalog chunk
   * 2. Updates the reactive signal (triggers re-renders)
   * 3. Applies HTML dir attribute
   * 4. Persists to localStorage
   */
  const setLocale = async (newLocale: SupportedLocale): Promise<void> => {
    if (newLocale === locale()) {
      return;
    }
    await applyLocale(newLocale);

    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch (err) {
      console.warn("[i18n] Could not persist locale to localStorage:", err);
    }
  };

  // On mount: load the initial locale (from localStorage or browser)
  onMount(() => {
    const init = async () => {
      try {
        const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
        const userLocale = isSupportedLocale(storedLocale) ? storedLocale : detectBrowserLocale();

        // `applyLocale` forces consumers to re-derive (catalogVersion)
        // even if `userLocale` is the signal's default "en"—a no-op set
        // that would leave the entire UI showing hash IDs in a production build.
        // Its token also discards THIS load if the user already changed language
        // while the import was in flight (slow startup under load).
        await applyLocale(userLocale);
      } catch (err) {
        console.warn("[i18n] Failed to initialize i18n:", err);
        // Only here: if the load was overtaken by a newer one, THAT request owns
        // `isLoading`, and turning it off here would misreport its state.
        setIsLoading(false);
      }
    };
    void init();
  });

  const translate = (
    message: string | MessageDescriptor,
    values?: Record<string, unknown>,
  ): string => {
    locale(); // Establish dynamic reactive dependency on SolidJS signal
    catalogVersion(); // Re-derive when a catalog finishes loading (see applyLocale)
    if (typeof message === "string") {
      // The compiled catalog uses HASH IDs (generateMessageId), like the
      // `msg`...`` macro. A plain string (`t("My Day")`) would look for id="My Day"
      // and fail to resolve (showing English in every language). We compute the
      // same ID as the macro so `t("literal")` translates like `t(msg`literal`)`,
      // with the text itself as fallback. (Fix for the "language not reflected" bug.)
      return i18n._({ id: generateMessageId(message), message, values });
    }
    // Values also apply to descriptors (they go INSIDE the descriptor in
    // Lingui's API): without them, messages like msg`...{{date}}...`
    // rendered an empty placeholder ("{}"). The descriptor is built in a
    // separate variable: Lingui's extractor cannot parse an inline spread
    // inside `i18n._()` and aborts file extraction.
    const descriptor: MessageDescriptor = {
      ...message,
      values: { ...message.values, ...values },
    };
    return i18n._(descriptor);
  };

  return (
    <I18nContext.Provider
      value={{ locale, setLocale, isLoading, t: translate, tDynamic: translate }}
    >
      {props.children}
    </I18nContext.Provider>
  );
};
